import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography, type SxProps, type Theme } from "@mui/material";
import type { OrderingContentBlock } from "../../types";
import { QuestionHeader } from "./question-header";
import { useInteractiveSubmit } from "../../hooks/useInteractiveSubmit";
import { useOptionTransfer } from "../../hooks/useOptionTransfer";
import {
  interactiveRootSx,
  optionsPoolSx,
  useInteractiveStyles,
} from "./styles";
import { OptionTransferTouchGhost } from "./option-transfer-touch-ghost";
import { useShuffledItems } from "../../hooks/useShuffledItems";
import type { AssessmentInteractiveModeProps } from "./assessment-mode";
import { notifyOfflineActionBlocked } from "../../utils/internetConnection";

const normalize = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

interface OrderingBlockProps extends AssessmentInteractiveModeProps {
  block: OrderingContentBlock;
  blockIndex: number;
  messageId: string;
  userId: string;
  tabId: string;
  onQuizAnswered?: () => void;
}

export const OrderingBlock: React.FC<OrderingBlockProps> = ({
  block,
  blockIndex,
  messageId,
  userId,
  tabId,
  onQuizAnswered,
  hideSubmitButton = false,
  hideFeedback = false,
  hideResultHighlight = false,
  isAssessmentMode = false,
  submitGetterRef,
  onCompletionChange,
}) => {
  const interactiveStyles = useInteractiveStyles(isAssessmentMode);
  const disabled = block.state === "disable";
  const revealResults = disabled && !hideResultHighlight;
  const containerRef = useRef<HTMLDivElement>(null);
  const slotCount = Math.max(1, block.slot_count);
  const { submit, isSubmitting, hasSubmitted, isBrowserLocked, isOffline } =
    useInteractiveSubmit(onQuizAnswered);
  const isInteractionLocked =
    isSubmitting || hasSubmitted || isBrowserLocked || isOffline;

  const transfer = useOptionTransfer({
    disabled: disabled || hasSubmitted,
    isBrowserLocked,
    isOffline,
    containerRef,
  });

  const [draftOrder, setDraftOrder] = useState<(string | null)[]>(() => {
    if (block.user_order?.length) {
      const slots = Array.from({ length: slotCount }, () => null as string | null);
      block.user_order.forEach((id, i) => {
        if (i < slotCount) slots[i] = id;
      });
      return slots;
    }
    return Array.from({ length: slotCount }, () => null);
  });

  useEffect(() => {
    const slots = Array.from(
      { length: slotCount },
      () => null as string | null,
    );
    block.user_order?.forEach((id, i) => {
      if (i < slotCount) slots[i] = id;
    });
    setDraftOrder(slots);
  }, [block.user_order, block.state, slotCount, tabId]);

  const slots = disabled
    ? Array.from({ length: slotCount }, (_, i) => block.user_order?.[i] ?? null)
    : draftOrder;

  const optionById = useMemo(
    () => new Map(block.options.map((o) => [o.id, o])),
    [block.options],
  );

  const shuffledOptions = useShuffledItems(
    block.options,
    messageId,
    blockIndex,
    tabId,
  );

  const usedInSlots = useMemo(
    () => new Set(slots.filter(Boolean) as string[]),
    [slots],
  );

  const poolOptions = useMemo(() => {
    if (disabled) return [];
    return shuffledOptions.filter((o) => !usedInSlots.has(o.id));
  }, [shuffledOptions, disabled, usedInSlots]);

  const allFilled = slots.every(Boolean);
  const isSubmitDisabled = !allFilled || isInteractionLocked;

  useEffect(() => {
    onCompletionChange?.(allFilled);
  }, [allFilled, onCompletionChange]);

  useEffect(() => {
    if (!submitGetterRef) return;
    submitGetterRef.current = () => {
      if (!allFilled) return null;
      return {
        interactionType: "ordering",
        payload: { user_order: slots.filter(Boolean) as string[] },
      };
    };
    return () => {
      submitGetterRef.current = null;
    };
  }, [submitGetterRef, allFilled, slots]);

  const placeInSlot = useCallback(
    (slotIndex: number, optionId: string) => {
      if (disabled) return;
      if (isOffline) {
        notifyOfflineActionBlocked();
        return;
      }
      setDraftOrder((prev) => {
        const next = [...prev];
        const existingIndex = next.findIndex((id) => id === optionId);
        if (existingIndex >= 0) next[existingIndex] = null;
        if (next[slotIndex] === optionId) {
          next[slotIndex] = null;
        } else {
          next[slotIndex] = optionId;
        }
        return next;
      });
    },
    [disabled, isOffline],
  );

  const clearSlot = useCallback(
    (slotIndex: number) => {
      if (disabled) return;
      if (isOffline) {
        notifyOfflineActionBlocked();
        return;
      }
      setDraftOrder((prev) => {
        if (!prev[slotIndex]) return prev;
        const next = [...prev];
        next[slotIndex] = null;
        return next;
      });
    },
    [disabled, isOffline],
  );

  const handleSlotClick = useCallback(
    (
      slotIndex: number,
      optionId: string | null,
      placeSelected: () => void,
    ) => {
      if (disabled) return;
      if (isOffline) {
        notifyOfflineActionBlocked();
        return;
      }
      if (transfer.selectedOptionId) {
        placeSelected();
        return;
      }
      if (optionId) {
        clearSlot(slotIndex);
      }
    },
    [disabled, isOffline, transfer.selectedOptionId, clearSlot],
  );

  const expectedOrderFromAnswers = block.answers ?? [];

  const getSlotHighlight = (slotIndex: number): "correct" | "incorrect" | undefined => {
    if (!revealResults) return undefined;

    const expectedRaw = expectedOrderFromAnswers[slotIndex];
    const actualId = slots[slotIndex];
    if (!expectedRaw || !actualId) return undefined;

    const expectedOption = optionById.get(expectedRaw);
    const actualOption = optionById.get(actualId);
    const expected = normalize(expectedOption?.id ?? expectedRaw);
    const actualIdNorm = normalize(actualId);
    const actualTextNorm = normalize(actualOption?.text);

    const isCorrect = actualIdNorm === expected || actualTextNorm === expected;
    return isCorrect ? "correct" : "incorrect";
  };

  const handleSubmit = async () => {
    if (!allFilled || disabled || hideSubmitButton || isInteractionLocked) {
      return;
    }
    await submit({
      messageId,
      userId,
      tabId,
      interactionType: "ordering",
      blockIndex,
      data: block.data,
      payload: { user_order: slots.filter(Boolean) as string[] },
    });
  };

  const poolHint = transfer.getPoolHint(
    "Drag items into slots. Tap a filled slot to remove it.",
    "Tap a filled slot to remove — or tap/drag an item into a slot",
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        ...interactiveRootSx,
        ...(transfer.isDragActive
          ? { overflow: "hidden", touchAction: "none" }
          : {}),
      }}
    >
      <QuestionHeader
        title={block.title}
        instruction={block.instruction}
        prompt={block.prompt}
        styles={interactiveStyles}
      />

      <Box
        sx={{
          ...interactiveStyles.cardSx,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {slots.map((optionId, index) => {
          const text = optionId ? optionById.get(optionId)?.text : null;
          const dropHandlers = transfer.getDropTargetHandlers(
            `slot-${index}`,
            (id) => placeInSlot(index, id),
          );
          const placeSelected = dropHandlers.onClick;

          return (
            <Box
              key={`slot-${index}`}
              sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: interactiveStyles.palette.purple,
                  minWidth: 20,
                  fontSize: "1rem",
                }}
              >
                {index + 1}
              </Typography>
              <Box
                sx={
                  [
                    interactiveStyles.orderingSlotSx(
                      !!text,
                      getSlotHighlight(index),
                      !disabled,
                    ),
                    transfer.isDropTargetActive(`slot-${index}`)
                      ? interactiveStyles.dropTargetActiveSx
                      : {},
                  ] as SxProps<Theme>
                }
                {...dropHandlers}
                onClick={() =>
                  handleSlotClick(index, optionId, placeSelected)
                }
              >
                {text ?? (disabled ? "—" : "Drop here")}
              </Box>
            </Box>
          );
        })}
      </Box>

      {!disabled ? (
        <Box>
          <Typography sx={interactiveStyles.poolLabelSx}>{poolHint}</Typography>
          <Box sx={optionsPoolSx}>
            {poolOptions.map((option) => {
              const poolHandlers = transfer.getPoolOptionHandlers(
                option.id,
                option.text,
              );
              return (
                <Box
                  key={option.id}
                  {...poolHandlers}
                  sx={interactiveStyles.getOptionChipSx(
                    transfer.isDragging(option.id),
                    transfer.getOptionHighlight(option.id),
                  )}
                >
                  {option.text}
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}

      {block.feedback && disabled && !hideFeedback ? (
        <Typography sx={{ fontSize: "0.875rem", color: "#374151" }}>
          {block.feedback}
        </Typography>
      ) : null}

      {!disabled && !hideSubmitButton ? (
        <Button
          variant="contained"
          disableElevation
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          sx={interactiveStyles.submitButtonSx(isSubmitDisabled)}
        >
          {isSubmitting ? "Submitting..." : "Submit Answer"}
        </Button>
      ) : null}

      {transfer.touchGhost ? (
        <OptionTransferTouchGhost
          text={transfer.touchGhost.text}
          x={transfer.touchGhost.relativeX}
          y={transfer.touchGhost.relativeY}
          sx={interactiveStyles.touchDragGhostSx}
        />
      ) : null}
    </Box>
  );
};
